/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import logo from '@assets/gmind.png';
import avatar from '@assets/default_user.png';
import collapse from '@assets/collapse.svg';
import ask from '@icons/brand-hipchat.svg';
import layout_grid from '@assets/layout-grid.svg';
import slideshow from '@assets/slideshow.svg';
import writing from '@assets/writing-sign.svg';
import image from '@assets/picture-in-picture.svg';
import speakerPhone from '@icons/speakerphone.svg';
import videoPlus from '@icons/video-plus.svg';
import { RxCross2 } from 'react-icons/rx';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
    AssessementImage,
    ContentImage,
    CurriculumImage,
    ImageGeneratorImage,
    SchoolLettersImage,
    VideoMakerImage,
} from '@src/layouts/components/sidebar/components/images';
import { LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { Menu } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import { AudioLines, BadgeDollarSign, BookOpen, Brain, FileUp, Globe, Link2, ListChecks, Mail, UserPlus } from 'lucide-react';

type OptionType = {
    icon: typeof ask | React.ReactNode;
    text: string;
    label: string;
    comingSoon?: boolean;
    element?: JSX.Element;
    children?: OptionType[];
};

// Move optionsValue outside the component to avoid re-creation on every render
// Platform super admin: manages admins and users, and isn't an admin of one school app (same rule as
// the backend's verifySuperAdmin).
const isSuperAdmin = (userLevelDetails: LevelRestrictions | null, isAppAdmin: boolean) =>
    !isAppAdmin && Boolean(userLevelDetails?.canManageAdmin && userLevelDetails?.canManageUsers);

const optionsValue = (userLevelDetails: LevelRestrictions | null, isAppAdmin = false) : OptionType[] => [
    ...(
        userLevelDetails?.canManageAdmin && userLevelDetails?.canManageTransactions && userLevelDetails?.canManageUsers ? [{ icon: ask, text: 'Dashboard', label: '/home/dashboard' }] : []
    ),
    ...(
        userLevelDetails?.canManageUsers ? [{ icon: writing, text: 'All Users', label: '/home/all-users', element: ContentImage() }] : []
    ),
    ...(
        userLevelDetails?.canManageAdmin ? [
            { icon: writing, text: 'Admins', label: '/home/admins', element: ContentImage() },
            { icon: <UserPlus className='w-4'/>, text: 'Apps Without Admin', label: '/home/system-app-admins', element: ContentImage() },
        ] : []
    ),
    { icon: image, text: 'Third Party Requests', label: '/home/third-party-requests', element: ImageGeneratorImage() },
    ...(
        userLevelDetails?.canManageSchoolAI ? [
            { icon: <Brain className='w-4'/>, text: 'School AI', label: '/home/school-ai', element: ImageGeneratorImage() },
        ] : []
    ),
    ...(
        userLevelDetails?.canManageSchoolAIPayments ? [
            { icon: <Link2 className='w-4'/>, text: 'School Payment Links', label: '/home/school-payment-links', element: ImageGeneratorImage() },
        ] : []
    ),
    // { icon: layout_grid, text: 'Users', label: '/home/users', element: CurriculumImage() },
    ...(
        userLevelDetails?.canManageAffilliates ? [
            { icon: image, text: 'Affilliates', label: '/home/affiliates', element: ImageGeneratorImage() },
        ] : []
    ),
    ...(
        userLevelDetails?.canManageResources ? [
            { icon: image, text: 'Resources', label: '/home/resources', element: ImageGeneratorImage() },
            { icon: <FileUp className='w-4'/>, text: 'WAEC Questions Import', label: '/home/waec-import', element: ImageGeneratorImage() },
            { icon: <ListChecks className='w-4'/>, text: 'WAEC Practice Questions', label: '/home/waec-practice', element: ImageGeneratorImage() },
            { icon: <AudioLines className='w-4'/>, text: 'Helpy Dataset Import', label: '/home/helpy-dataset-import', element: ImageGeneratorImage() },
        ] : []
    ),
    ...(
        userLevelDetails?.canManageTransactions ? [
            { icon: videoPlus, text: 'Transactions', label: '/home/payout', element: VideoMakerImage() },
        ] : []
    ),
    ...(
        isSuperAdmin(userLevelDetails, isAppAdmin) ? [
            { icon: <Mail className='w-4'/>, text: 'Email Marketing', label: '/home/email-marketing', element: ContentImage() },
            { icon: <Globe className='w-4'/>, text: 'Tenant Domains', label: '/home/tenant-domains', element: ContentImage() },
            { icon: <BadgeDollarSign className='w-4'/>, text: 'ClassHub Pricing', label: '/home/classhub-pricing', element: ContentImage() },
        ] : []
    ),
    ...(
        userLevelDetails?.canManageAdmin ? [
            { icon: writing, text: 'Permission', label: '/home/permission', element: ContentImage() },
        ] : []
    ),
    { icon: slideshow, text: 'Log Out', label: '/auth', element: <LogoutOutlined /> },
];

const Sidebar = React.memo(
    ({
        selected,
        className,
        style,
        onMobileClose,
        collapsed,
        isMobile,
        userIsAffiliate,
        hasPendingAffiliateRequest,
        onBecomeAffiliate,
        userDetails
    }: {
        selected: (v: string) => void;
        className?: string;
        style?: React.CSSProperties;
        collapsed?: (v: boolean) => void;
        onMobileClose?: () => void;
        isMobile?: boolean;
        userIsAffiliate?: boolean;
        hasPendingAffiliateRequest?: boolean;
        onBecomeAffiliate?: () => void;
        userDetails: AdminDetailsProps | null;
    }) => {
        // const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const [isCollapsed, setCollapsed] = useState(false);

        // the mobile drawer is always shown expanded
        useEffect(() => {
            if (isMobile) setCollapsed(false);
        }, [isMobile]);
        const navigate = useNavigate();
        const [userOptions, setUserOptions] = useState(false);
        const [selectedOption, setSelectedOption] = useState<string>('');
        // const { userDetails } = useUserContext();
        const [openKeys, setOpenKeys] = useState<string[]>([]);
        const [affiliate, setAffiliate] = useState('Become an Affiliate');

        const [showBecomeAnAffiliate, setShowBecomeAnAffiliate] = useState<boolean>(false);
        const [showPendingAffiliate, setShowPendingAffiliate] = useState<boolean>(false);
        const path = window.location.pathname;

        useEffect(() => {
            setShowBecomeAnAffiliate(userIsAffiliate ?? false);
            setShowPendingAffiliate(hasPendingAffiliateRequest ?? false);
        }, [userIsAffiliate, hasPendingAffiliateRequest]);



        const openAffiliateModal = () => {
            if (onBecomeAffiliate) {
                onBecomeAffiliate();
            }
        };



        const handleSelect = useCallback(
            (label: string,) => {
                setSelectedOption(label);
                selected(label);

                const parentKey = optionsValue(userDetails?.levelRestrictions || null, Boolean(userDetails?.specifcAppAdmin)).find((option) =>
                    option.children?.some((child) => child.label === label),
                )?.label;
                if (parentKey && !openKeys.includes(parentKey)) {
                    setOpenKeys([...openKeys, parentKey]);
                }
                if (label === 'chat') {
                    localStorage.removeItem('activeChat');
                }
            },
            [selected, openKeys],
        );

        useEffect(() => {
            const savedOpenKeys = localStorage.getItem('openKeys');
            if (savedOpenKeys) {
                setOpenKeys(JSON.parse(savedOpenKeys));
            }
        }, []);

        const handleOpenChange = (keys: string[]) => {
            setOpenKeys(keys); // Update the open keys state
            localStorage.setItem('openKeys', JSON.stringify(keys)); // Save open keys to localStorage
        };

        useEffect(() => {
            const { pathname } = window.location;
            const selected = pathname.split('/').slice(-1);
            setSelectedOption(selected[0]);
        }, []);
        const handleNavigate = (label: string) => {
            setSelectedOption(label); // Update the selected option
            // Navigate to the corresponding route based on the label
            switch (label) {
                case 'dashboard':
                    navigate('/home/dashboard');
                    break;
                case 'users':
                    navigate('/home/users');
                    break;
                case 'admins':
                    navigate('/home/admins');
                    break;
                case 'system-app-admins':
                    navigate('/home/system-app-admins');
                    break;
                case 'affiliates':
                    navigate('/home/affiliates');
                    break;
                case 'third-party-requests':
                    navigate('/home/third-party-requests');
                    break;
                case 'school-ai':
                    navigate('/home/school-ai');
                    break;
                case 'school-payment-links':
                    navigate('/home/school-payment-links');
                    break;
                case 'corporates':
                    navigate('/home/corporates');
                    break;
                case 'schools':
                    navigate('/home/schools');
                    break;
                case 'payout':
                    navigate('/home/payout');
                    break;
                case 'ticket':
                    navigate('/home/ticket');
                    break;
                case 'resources':
                    navigate('/home/resources');
                    break;
                case 'waec-import':
                    navigate('/home/waec-import');
                    break;
                case 'waec-practice':
                    navigate('/home/waec-practice');
                    break;
                case 'helpy-dataset-import':
                    navigate('/home/helpy-dataset-import');
                    break;
                case 'permission':
                    navigate('/home/all-permissions');
                    break;
                case 'logout':
                    navigate('/auth'); // Adjust as needed for logout
                    break;
                default:
                    break;
            }
        };

        // useEffect(() => {
        //     if (window.innerWidth <= 820 && !isMobile) {
        //         setCollapsed(true);
        //     }
        // }, [window.innerWidth]);

        return (
            <nav
                style={style}
                className={
                    'relative transition-all bg-white h-full sidebar flex flex-col px-0 pb-0 shadow-lg border-r border-gray-200 ' +
                    className
                }
            >
                <div

                    style={

                        isCollapsed ? { justifyContent: 'center' } : { justifyContent: 'space-between'}
                    }
                    className="flex px-6 py-6 h-20 items-center bg-white relative border-b border-gray-200 "
                >
                    {!isCollapsed && (
                        <img
                            onClick={() => {
                                navigate('/');
                            }}
                            role="button"
                            src={logo}
                            className="w-[8rem]"
                        />
                    )}
                    {isMobile && (
                        <RxCross2
                            role="button"
                            onClick={onMobileClose}
                            className="absolute text-2xl top-0 right-5 text-[#48474b]"
                        />
                    )}
                    <div
                        role="button"
                        onClick={() => {
                            setCollapsed(!isCollapsed);
                            if (collapsed) {
                                collapsed(!isCollapsed);
                            }
                        }}
                    >
                        {!isMobile && (
                            <img
                                style={isCollapsed ? { width: '1rem' } : {}}
                                role="button"
                                src={collapse}
                                className="max-md:hidden w-[1.5rem]"
                            />
                        )}
                    </div>
                </div>


                <div className="relative h-full px-3 mt-4 z-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    <Menu
                        mode="inline"
                        selectedKeys={[selectedOption]}
                        openKeys={openKeys} // Control open submenus
                        onOpenChange={handleOpenChange}
                        className="mt-2 text-[14px] bg-transparent"
                        style={{
                            fontSize: '14px',
                            height: '85%',
                            borderRight: 0,
                            backgroundColor: 'transparent',
                            padding: '0px',
                            margin: "0px"
                        }}
                    >
                        {optionsValue(userDetails?.levelRestrictions || null, Boolean(userDetails?.specifcAppAdmin)).map(({ icon, text, label, children }) =>
                            children && children.length > 0 ? (
                                <Menu.SubMenu
                                    key={label}
                                    icon={typeof icon === 'string' ? <img className="w-[0.7rem]" src={icon} alt="icon" /> : icon}
                                    title={<span className="text-black text-[16px]">{!isCollapsed && text}</span>}
                                >
                                    {children.map(({ icon: childIcon, text: childText, label: childLabel }) => (
                                        <Menu.Item
                                            key={childLabel}
                                            icon={typeof childIcon === 'string' ? <img className={`w-3 `} src={childIcon} alt="icon" /> : childIcon}
                                            onClick={() => handleSelect(childLabel)}
                                            style={{
                                                backgroundColor:
                                                    selectedOption === childLabel
                                                        ? '#dad9df'
                                                        : 'transparent',
                                                color: selectedOption === childLabel ? '#000' : '#000',
                                            }}
                                        >
                                            <Link to={label}>  {!isCollapsed && childText}</Link>
                                        </Menu.Item>
                                    ))}
                                </Menu.SubMenu>
                            ) : (
                                <Menu.Item
                                    key={label}
                                    onClick={() => {
                                        handleNavigate(selectedOption)
                                        handleSelect(label)
                                    }}
                                    icon={typeof icon === 'string' ? <img className="w-4" src={icon} alt="icon" /> : icon}
                                    style={{
                                        paddingRight: "0px",
                                        backgroundColor:
                                            selectedOption === label ? 'rgba(229, 81, 9, 0.1)' : 'transparent',
                                        color: selectedOption === label ? "#E55109" : '#6B7280',
                                        borderLeft: selectedOption === label ? '3px solid #E55109' : '3px solid transparent',
                                        borderRadius: '8px',
                                        marginBottom: '4px',
                                        transition: 'all 0.2s ease',
                                    }}
                                    className="relative flex items-center gap-2 hover:bg-gray-50 hover:text-gray-900"
                                >
                                    {!isCollapsed && (
                                        <Link to={label} className="flex items-center gap-3">
                                            {text}
                                            <span className='' style={{ zIndex: 2000 }}>
                                                {' '}
                                                {label === 'video_mode' && (
                                                    <div
                                                        style={{}}
                                                        className="   overflow-hidden h-6 font-bold text-[0.5rem] text-[#FFFFFF80] text-center flex flex-col p-2 justify-center rounded-[3rem] bg-[#dad9df]"
                                                    >
                                                        Coming soon
                                                    </div>
                                                )}
                                            </span>
                                        </Link>
                                    )}
                                </Menu.Item>
                            ),
                        )}
                    </Menu>
                </div>




                <section
                    role="button"
                    style={isCollapsed ? { userSelect: 'none', } : {  }}
                    onClick={() => navigate('/home/profile')}
                    className="bg-gray-50 relative mt-auto flex flex-col select-none transition-all h-[4.5rem] p-4 cursor-pointer gap-4 border-t border-gray-200 hover:bg-gray-100"
                >
                    <div className="flex items-start gap-3 transition-all">
                        <img
                            src={avatar}
                            className="bg-center bg-cover rounded-full w-9 h-9 max-lg:w-7 border-2 border-gray-300"
                            style={
                                isCollapsed
                                    ? {
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }
                                    : {}
                            }
                        />
                        {!isCollapsed && (
                            <div className="flex items-center justify-between w-full">
                                <div className="flex flex-col">
                                    <p className="font-semibold text-sm text-gray-900 max-lg:text-[0.65rem]">
                                        {userDetails?.fullname ?? 'User'}
                                    </p>
                                    <p className="text-[0.65rem] max-lg:hidden text-gray-500">{userDetails?.email ?? ''}</p>
                                </div>

                                <SettingOutlined className="space-x-2 text-lg !text-customOrange hover:rotate-90 transition-transform duration-300" style={{ color: '#E55109' }} />
                            </div>
                        )}
                    </div>
                </section>
            </nav>
        );
    },
);

export default Sidebar;
